import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { getPaymentStatus, getSession } from "../lib/api";
import { useSession } from "../context/SessionContext";

function Shell({ children, testId }) {
  return (
    <div className="min-h-[100dvh] bg-[#f8faf8]">
      <BrandHeader title="GreenFlow Landscape Vision" subtitle="Secure checkout" />
      <main className="mx-auto max-w-md px-5 py-12 text-center gf-fade-in" data-testid={testId}>{children}</main>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  const { session } = useSession();
  const diag = session?.pathway === "diagnose";
  return (
    <Shell testId="payment-cancel-page">
      <XCircle className="mx-auto text-slate-400" size={44} />
      <h1 className="mt-4 font-display text-2xl font-bold text-[#092C15]">Checkout canceled</h1>
      <p className="mt-2 text-sm text-slate-600">No payment was taken and nothing was generated. Your submission and concepts are saved.</p>
      <Button data-testid="payment-back-button" onClick={() => navigate(diag ? "/diagnose" : "/improve")} className="mt-6 rounded-xl h-12 px-6 bg-emerald-600 hover:bg-emerald-700">
        <ArrowLeft size={16} className="mr-2" /> {diag ? "Back to My Diagnosis" : "Back to My Landscape Vision"}
      </Button>
    </Shell>
  );
}

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("session_id");
  const [state, setState] = useState("checking");
  const [item, setItem] = useState("");
  const { session, setSession } = useSession();
  const back = item === "diagnostic" || session?.pathway === "diagnose" ? "/diagnose" : "/improve";

  useEffect(() => {
    if (!id) {
      setState("error");
      return undefined;
    }
    let tries = 0;
    let alive = true;
    const poll = async () => {
      try {
        const s = await getPaymentStatus(id);
        if (!alive) return;
        if (s.payment_status === "paid") {
          if (s.order_status === "needs_refund") return setState("refund");
          setItem(s.item);
          setState("paid");
          if (s.item === "diagnostic") setSession(await getSession(s.submission_id).catch(() => session));
          else if (!session) setSession(await getSession(s.submission_id).catch(() => null));
          setTimeout(() => navigate(s.item === "diagnostic" ? "/diagnose" : "/improve"), 1800);
          return;
        }
        if (s.status === "expired") return setState("expired");
      } catch {
        /* retry */
      }
      tries += 1;
      if (tries < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => {
      alive = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Shell testId="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="mx-auto animate-spin text-emerald-600" size={40} />
          <h1 className="mt-4 font-display text-2xl font-bold text-[#092C15]">{id?.startsWith("admin_") ? "Creating your complimentary item…" : "Confirming your payment…"}</h1>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-emerald-600" size={44} />
          <h1 data-testid="payment-confirmed" className="mt-4 font-display text-2xl font-bold text-[#092C15]">Payment confirmed</h1>
          <p className="mt-2 text-sm text-slate-600">{item === "diagnostic" ? "Dr. Verdant™ will now review your photos. Taking you back…" : "Your new GreenFlow concept is being created. Taking you back…"}</p>
        </>
      )}
      {state === "refund" && (
        <>
          <XCircle className="mx-auto text-amber-500" size={44} />
          <h1 data-testid="payment-refund-needed" className="mt-4 font-display text-2xl font-bold text-[#092C15]">Payment received — already created</h1>
          <p className="mt-2 text-sm text-slate-600">This view or revision already exists for your project, so nothing new was generated. GreenFlow has been notified and will refund this payment.</p>
        </>
      )}
      {["expired", "timeout", "error"].includes(state) && (
        <>
          <XCircle className="mx-auto text-amber-500" size={44} />
          <h1 data-testid="payment-unconfirmed" className="mt-4 font-display text-2xl font-bold text-[#092C15]">We couldn't confirm this payment yet</h1>
          <p className="mt-2 text-sm text-slate-600">Nothing will be generated until payment is verified. If you were charged, GreenFlow will make it right.</p>
        </>
      )}
      {state !== "checking" && state !== "paid" && (
        <Button data-testid="payment-result-back-button" onClick={() => navigate(back)} className="mt-6 rounded-xl h-12 px-6 bg-emerald-600 hover:bg-emerald-700">Back</Button>
      )}
    </Shell>
  );
}
